/* ==========================================================================
   Panier Dawn V2 : tiroir panier (minuteur, ventes additionnelles, case des conditions)
   et champ « code de réduction » (page panier et tiroir).
   ========================================================================== */

/* ---------- Appels à l'API panier de Shopify ---------- */
/* kind = add | change | update ; renvoie le JSON de Shopify. */
window.v2CartRequest = function (kind, body) {
  return fetch(`${window.routes[`cart_${kind}_url`]}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => r.json());
};

/* ---------- Rafraîchir le tiroir panier ----------
   Une fois le panier modifié, on redemande à Shopify le tiroir et la pastille du panier (rendu serveur). */
window.v2RefreshCartDrawer = async function () {
  const drawer = document.querySelector('cart-drawer');
  const inner = drawer && drawer.querySelector('.drawer__inner');
  if (!inner) return;

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  let fresh = null;
  let bubbleHtml = '';
  // on réessaie une fois si Shopify répond vide (réponse perdue)
  for (let attempt = 0; attempt < 2 && !fresh; attempt++) {
    if (attempt) await new Promise((resolve) => setTimeout(resolve, 600));
    const stamp = Date.now();
    const [drawerHtml, bubble] = await Promise.all(
      ['cart-drawer', 'cart-icon-bubble'].map((id) =>
        fetch(`${window.routes.cart_url}?section_id=${id}&t=${stamp}`).then((r) => r.text())
      )
    );
    fresh = parse(drawerHtml).querySelector('.drawer__inner');
    bubbleHtml = bubble;
  }
  if (fresh) {
    inner.innerHTML = fresh.innerHTML;
    drawer.classList.toggle('is-empty', !!fresh.querySelector('.drawer__inner-empty'));
  }
  const bubble = parse(bubbleHtml).querySelector('.shopify-section');
  const bubbleTarget = document.getElementById('cart-icon-bubble');
  if (bubble && bubbleTarget) bubbleTarget.innerHTML = bubble.innerHTML;

  if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
    const cart = await fetch(`${window.routes.cart_url}.js`).then((r) => r.json());
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'v2-panier', cartData: cart });
  }
};

/* ---------- Code de réduction ---------- */
if (!customElements.get('v2-cart-discount')) {
  customElements.define(
    'v2-cart-discount',
    class V2CartDiscount extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input');
        this.error = this.querySelector('.cart-discount-form__error');
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.apply();
        });
        this.input.addEventListener('input', () => this.showError(''));
      }

      showError(message) {
        this.error.textContent = message;
        this.error.style.display = message ? 'block' : 'none';
      }

      async apply() {
        const code = this.input.value.trim();
        if (!code) return this.showError(this.dataset.errorEmpty);

        const known = (this.dataset.codes || '').split(',').filter(Boolean);
        const button = this.querySelector('button');
        button.disabled = true;
        try {
          const cart = await window.v2CartRequest('update', {
            discount: [...new Set([...known, code])].join(','),
          });
          // Shopify garde le code dans sa liste même s'il est refusé (applicable: false) : on le retire alors.
          const entry = (cart.discount_codes || []).find((item) => item.code.toLowerCase() === code.toLowerCase());
          if (!entry || !entry.applicable) {
            await window.v2CartRequest('update', { discount: known.join(',') });
            return this.showError(this.dataset.errorInvalid);
          }

          if (this.dataset.context === 'drawer') await window.v2RefreshCartDrawer();
          else window.location.reload();
        } catch (e) {
          this.showError(this.dataset.errorInvalid);
        } finally {
          button.disabled = false;
        }
      }
    }
  );
}

/* ---------- Minuteur « panier réservé » ---------- */
if (!customElements.get('v2-cart-timer')) {
  customElements.define(
    'v2-cart-timer',
    class V2CartTimer extends HTMLElement {
      connectedCallback() {
        const duration = Math.max(parseInt(this.dataset.duration, 10) || 600, 1);
        const key = 'v2-cart-timer-end';
        let end = 0;
        try {
          end = parseInt(sessionStorage.getItem(key), 10) || 0;
        } catch (e) {}
        // Le compte à rebours continue d'une page à l'autre ; arrivé à zéro, il repart.
        if (end <= Date.now() || end - Date.now() > duration * 1000) {
          end = Date.now() + duration * 1000;
          try {
            sessionStorage.setItem(key, String(end));
          } catch (e) {}
        }
        this.end = end;
        this.duration = duration;
        this.key = key;
        this.render();
        this.interval = setInterval(() => this.render(), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.interval);
      }

      render() {
        let left = Math.round((this.end - Date.now()) / 1000);
        if (left <= 0) {
          this.end = Date.now() + this.duration * 1000;
          try {
            sessionStorage.setItem(this.key, String(this.end));
          } catch (e) {}
          left = this.duration;
        }
        const pad = (n) => String(n).padStart(2, '0');
        const hours = Math.floor(left / 3600);
        const minutes = Math.floor((left % 3600) / 60);
        const seconds = left % 60;
        const parts = hours > 0 ? [hours, minutes, seconds] : [minutes, seconds];
        this.innerHTML = parts.map((n) => `<span>${pad(n)}</span>`).join(':');
      }
    }
  );
}

/* ---------- Ventes additionnelles du tiroir ---------- */
if (!customElements.get('v2-cart-upsell')) {
  customElements.define(
    'v2-cart-upsell',
    class V2CartUpsell extends HTMLElement {
      connectedCallback() {
        this.handle = this.dataset.handle;
        this.style_ = this.dataset.style;
        this.variants = [];
        try {
          this.variants = JSON.parse(this.querySelector('script[data-variants]').textContent);
        } catch (e) {}
        this.pickers = [...this.querySelectorAll('select[data-option]')];

        this.addEventListener('click', (event) => this.onClick(event));
        this.pickers.forEach((select) => select.addEventListener('change', () => this.onVariantChange()));
        if (this.pickers.length) this.syncVariant();
        this.autoSelect();
      }

      get selected() {
        return this.dataset.selected === 'true';
      }

      onClick(event) {
        if (event.target.closest('select, a')) return;
        if (this.style_ === 'add_button') {
          if (event.target.closest('[data-add]')) this.add();
          return;
        }
        const onButton = event.target.closest('[data-toggle]');
        const onContainer = event.target.closest('.v2-cd-upsell__container');
        if (this.dataset.toggleMode === 'container' ? onContainer : onButton) this.toggle();
      }

      /* variante choisie dans les listes */
      currentVariant() {
        const chosen = this.pickers.map((select) => select.value);
        return this.variants.find((v) => v.options.every((value, i) => value === chosen[i]));
      }

      syncVariant() {
        const variant = this.currentVariant();
        if (!variant) return;
        this.dataset.variantId = variant.id;
        if (this.dataset.updatePrices === 'true') {
          this.querySelectorAll('[data-price]').forEach((el) => (el.textContent = variant.price));
          this.querySelectorAll('[data-compare]').forEach((el) => {
            el.textContent = variant.compare;
            el.hidden = !variant.onSale;
          });
        }
        const addButton = this.querySelector('[data-add]');
        if (addButton) addButton.disabled = !variant.available;
      }

      async onVariantChange() {
        this.syncVariant();
        // le produit est déjà dans le panier : on remplace sa variante
        if (this.selected) await this.apply({ remove: true, add: true });
      }

      toggle() {
        return this.apply({ remove: this.selected, add: !this.selected });
      }

      add() {
        return this.apply({ add: true });
      }

      /* retire et/ou ajoute le produit, puis rafraîchit le tiroir (qui se redessine à partir du panier) */
      async apply({ remove, add }) {
        if (this.busy) return;
        this.busy = true;
        this.classList.add('is-loading');
        try {
          if (remove) {
            const cart = await fetch(`${window.routes.cart_url}.js`).then((r) => r.json());
            for (const line of cart.items.filter((item) => item.handle === this.handle)) {
              await window.v2CartRequest('change', { id: line.key, quantity: 0 });
            }
          }
          if (add) {
            await window.v2CartRequest('add', { items: [{ id: Number(this.dataset.variantId), quantity: 1 }] });
          }
          await window.v2RefreshCartDrawer();
        } catch (e) {
          console.error(e);
        } finally {
          this.busy = false;
          this.classList.remove('is-loading');
        }
      }

      /* « Coché au départ » : ajouté une seule fois, si le panier n'est pas vide */
      async autoSelect() {
        if (this.dataset.preselected !== 'true' || this.selected || this.style_ === 'add_button') return;
        const flag = `v2-upsell-auto-${this.handle}`;
        try {
          if (sessionStorage.getItem(flag)) return;
          sessionStorage.setItem(flag, '1');
        } catch (e) {
          return;
        }
        const cart = await fetch(`${window.routes.cart_url}.js`).then((r) => r.json());
        if (cart.item_count > 0) await this.apply({ add: true });
      }
    }
  );
}

/* ---------- Case « J'accepte les conditions » ---------- */
if (!customElements.get('v2-cart-tnc')) {
  customElements.define(
    'v2-cart-tnc',
    class V2CartTnc extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.button.addEventListener('click', () => this.setChecked(!this.checked));
        this.checkout = document.getElementById('CartDrawer-Checkout');
        this.dim();
        // capture : on passe avant l'envoi du formulaire de paiement
        this.guard = (event) => {
          if (this.checked) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.warn();
        };
        if (this.checkout) this.checkout.addEventListener('click', this.guard, true);
      }

      disconnectedCallback() {
        if (this.checkout) this.checkout.removeEventListener('click', this.guard, true);
      }

      get checked() {
        return this.dataset.checked === 'true';
      }

      setChecked(value) {
        this.dataset.checked = String(value);
        this.button.setAttribute('aria-checked', String(value));
        this.dim();
        if (value) this.clearWarnings();
      }

      dim() {
        if (this.checkout && this.dataset.disableButton === 'true') {
          this.checkout.style.opacity = this.checked ? '' : '0.5';
        }
      }

      warnings() {
        return [...this.closest('.drawer__footer').querySelectorAll('.tnc-checkbox-warning')];
      }

      clearWarnings() {
        this.warnings().forEach((el) => {
          el.textContent = '';
          el.classList.add('hidden');
        });
      }

      warn() {
        this.clearWarnings();
        const target = this.warnings().find((el) => el.classList.contains(`tnc-checkbox-warning--${this.dataset.warningPosition}`));
        if (!target) return;
        target.textContent = this.dataset.warningText;
        target.classList.remove('hidden');
      }
    }
  );
}

/* ---------- Cadeau du tiroir ----------
   Quand l'objectif est atteint (data-unlocked), le cadeau s'ajoute tout seul au panier ; sinon il s'en retire. */
if (!customElements.get('v2-cart-gift')) {
  customElements.define(
    'v2-cart-gift',
    class V2CartGift extends HTMLElement {
      connectedCallback() {
        this.handle = this.dataset.handle;
        this.variants = [];
        try {
          this.variants = JSON.parse(this.querySelector('script[data-variants]').textContent);
        } catch (e) {}
        this.pickers = [...this.querySelectorAll('select[data-option]')];
        this.pickers.forEach((select) => select.addEventListener('change', () => this.onVariantChange()));
        this.sync();
      }

      currentVariant() {
        const chosen = this.pickers.map((select) => select.value);
        return this.variants.find((v) => v.options.every((value, i) => value === chosen[i]));
      }

      async onVariantChange() {
        const variant = this.currentVariant();
        if (!variant) return;
        this.dataset.variantId = variant.id;
        // le cadeau est déjà dans le panier : on remplace sa variante
        if (this.dataset.inCart === 'true') await this.apply({ remove: true, add: true });
      }

      /* le panier doit refléter l'état « débloqué / bloqué » */
      async sync() {
        const unlocked = this.dataset.unlocked === 'true';
        const inCart = this.dataset.inCart === 'true';
        if (unlocked === inCart) return;
        try {
          // un essai a échoué (produit épuisé...) : on ne réessaie pas avant une minute
          if (Date.now() - (parseInt(sessionStorage.getItem(`v2-gift-failed-${this.handle}`), 10) || 0) < 60000) return;
          // garde-fou : au plus un essai toutes les 3 secondes ; si on est trop tôt, on repasse juste après
          const last = parseInt(sessionStorage.getItem(`v2-gift-${this.handle}`), 10) || 0;
          const wait = 3000 - (Date.now() - last);
          if (wait > 0) {
            setTimeout(() => {
              const current = document.querySelector(`v2-cart-gift[data-handle="${this.handle}"]`);
              if (current) current.sync();
            }, wait + 50);
            return;
          }
          sessionStorage.setItem(`v2-gift-${this.handle}`, String(Date.now()));
        } catch (e) {}
        await this.apply({ remove: !unlocked, add: unlocked });
      }

      async apply({ remove, add }) {
        if (this.busy) return;
        this.busy = true;
        this.classList.add('is-loading');
        try {
          if (remove) {
            const cart = await fetch(`${window.routes.cart_url}.js`).then((r) => r.json());
            for (const line of cart.items.filter((item) => item.handle === this.handle)) {
              await window.v2CartRequest('change', { id: line.key, quantity: 0 });
            }
          }
          if (add) {
            const variant = Number(this.dataset.variantId);
            const result = await window.v2CartRequest('add', { items: [{ id: variant, quantity: 1 }] });
            if (result.status) throw new Error(result.description || result.message); // ajout refusé (épuisé...)
          }
          await window.v2RefreshCartDrawer();
        } catch (e) {
          console.error(e);
          try {
            sessionStorage.setItem(`v2-gift-failed-${this.handle}`, String(Date.now()));
          } catch (err) {}
        } finally {
          this.busy = false;
          this.classList.remove('is-loading');
        }
      }
    }
  );
}
